// src/components/AddUserModal.jsx
import React, { useState } from "react";
import { apiFetch } from "../api";
import HeightSelector from "./HeightSelector";
import { useLoading } from "../contexts/LoadingContext";
import { SaveIcon, CancelIcon } from "./icons";

const initialFormState = {
  fullName: "",
  gender: "",
  age: "",
  city: "",
  country: "Pakistan",
  contactNumber: "",
  profession: "",
  monthlyIncome: "",
  cast: "",
  caste: "",
  maritalStatus: "single",
  dob: "",
  height: "",
  heightFeet: "",
  heightInches: "",
  education: "",
  instituteOfQualification: "",
  // siblings counts are derived from structured `siblings` array

  siblings: [],
  fatherOccupation: "",
  motherOccupation: "",
  fatherDeceased: false,
  motherDeceased: false,
  houseSizeMarla: "",
  houseLocation: "",
  area: "",
  houseStatus: "",
  preferences: "",
  views: 0,
  status: "active",
  // Match preferences (used when this profile is referred-to)
  clientNotes: "",
  preferredGender: "any",
  preferredAgeMin: "",
  preferredAgeMax: "",
  preferredCountries: "",
  preferredCities: "",
  preferredProfessions: "",
  preferredCastes: "",
  preferredHeightMinFeet: "",
  preferredHeightMinInches: "",
  preferredHeightMaxFeet: "",
  preferredHeightMaxInches: "",
};

export default function AddUserModal({ isOpen, onClose, onSaved }) {
  const [form, setForm] = useState(initialFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const { setLoading, setGlobalLoadingMessage } = useLoading();

  const handleChange = (e) => {
    // Handle custom HeightSelector events
    if (e.target && e.target.heightFeet !== undefined && e.target.heightInches !== undefined) {
      // This is a HeightSelector event - handle specially
      const { value, heightFeet, heightInches } = e.target;
      setForm((p) => ({
        ...p,
        height: value,
        heightFeet: heightFeet,
        heightInches: heightInches
      }));
      return;
    }

    // Handle normal form events
    const { name, value, type, checked } = e.target;
    const nextVal = type === "checkbox" ? checked : value;

    // If updating cast or caste, synchronize both fields
    if (name === "cast" || name === "caste") {
      setForm((p) => ({
        ...p,
        cast: nextVal,
        caste: nextVal
      }));
    } else if (name === "gender") {
      // Matrimonial pairing is always cross-gender — derive the
      // preferred gender from the profile's own gender. The bureau
      // can still override manually in the preferences section, but
      // the default reflects the obvious case so they don't have to.
      const opposite =
        nextVal === "male" ? "female" : nextVal === "female" ? "male" : null;
      setForm((p) => ({
        ...p,
        gender: nextVal,
        ...(opposite ? { preferredGender: opposite } : {}),
      }));
    } else {
      setForm((p) => ({ ...p, [name]: nextVal }));
    }
  };

  const handleAgeChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((p) => ({ ...p, age: "" }));
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    // Hard-cap during typing at 100. Lower bound is enforced on blur so
    // users can still type "1" → "18" without it snapping mid-keystroke.
    const clamped = num > 100 ? 100 : num;
    setForm((p) => ({ ...p, age: String(clamped) }));
  };

  const handleAgeBlur = () => {
    if (form.age === "" || form.age === undefined || form.age === null) return;
    const num = Number(form.age);
    if (Number.isNaN(num)) return;
    if (num < 18) setForm((p) => ({ ...p, age: "18" }));
    else if (num > 100) setForm((p) => ({ ...p, age: "100" }));
  };

  // Shared clamping helpers for the preferred-age range fields. Hard-caps
  // at 80 during typing so users can't overshoot, and snaps to 18 on blur
  // if they leave something below the floor. Negatives can't be typed
  // because the keypress filter rejects the minus sign.
  const handlePreferredAgeChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm((p) => ({ ...p, [field]: "" }));
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const clamped = num > 80 ? 80 : num < 0 ? 0 : num;
    setForm((p) => ({ ...p, [field]: String(clamped) }));
  };
  const handlePreferredAgeBlur = (field) => () => {
    const v = form[field];
    if (v === "" || v === undefined || v === null) return;
    const num = Number(v);
    if (Number.isNaN(num)) return;
    if (num < 18) setForm((p) => ({ ...p, [field]: "18" }));
    else if (num > 80) setForm((p) => ({ ...p, [field]: "80" }));
  };
  const blockNonNumericKey = (e) => {
    // Block "-", "+", and "e" (scientific notation) — number inputs would
    // otherwise let users type negative or exponent values that the
    // change handler can't recover from cleanly.
    if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
  };

  const handleSiblingChange = (index, field, value) => {
    setForm((p) => {
      const next = { ...p };
      next.siblings = next.siblings ? [...next.siblings] : [];
      next.siblings[index] = {
        ...(next.siblings[index] || {}),
        [field]: value,
      };
      return next;
    });
  };

  const addSibling = () => {
    setForm((p) => ({
      ...p,
      siblings: [
        ...(p.siblings || []),
        {
          relation: "brother",
          qualification: "",
          profession: "",
          maritalStatus: "",
        },
      ],
    }));
  };

  const removeSibling = (index) => {
    setForm((p) => {
      const next = { ...p };
      next.siblings = [...(next.siblings || [])];
      next.siblings.splice(index, 1);
      return next;
    });
  };

  const validate = () => {
    // Gender required — otherwise the profile card renders without a
    // badge and downstream matchmaking can't pair the profile.
    if (!form.gender) {
      return "Please select gender";
    }

    // Contact number is now OPTIONAL. Bureaus that consider their phone
    // list a proprietary asset can leave it blank and introduce parties
    // manually after approval. Format is only checked when provided.
    const phone = form.contactNumber?.trim() || "";
    if (phone && !/^\d{11}$/.test(phone)) {
      return "Contact number must be exactly 11 digits when provided";
    }

    // Age, when provided, must be 18-100
    if (form.age !== "" && form.age !== undefined && form.age !== null) {
      const ageNum = Number(form.age);
      if (Number.isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
        return "Age must be between 18 and 100";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setAttemptedSubmit(true);
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }

    // compute counts either from structured siblings array or explicit counts
    const siblingsArray = Array.isArray(form.siblings) ? form.siblings : [];
    const computedBrothers = siblingsArray.filter(
      (s) => s.relation === "brother",
    ).length;
    const computedSisters = siblingsArray.filter(
      (s) => s.relation === "sister",
    ).length;

    const csvToArray = (s) =>
      typeof s === "string"
        ? s.split(",").map((x) => x.trim()).filter(Boolean)
        : [];

    const {
      preferredAgeMin,
      preferredAgeMax,
      preferredCountries,
      preferredCities,
      preferredProfessions,
      preferredCastes,
      preferredHeightMinFeet,
      preferredHeightMinInches,
      preferredHeightMaxFeet,
      preferredHeightMaxInches,
      ...rest
    } = form;

    const toTotalInches = (feet, inches) => {
      const f = Number(feet);
      const i = Number(inches);
      if (Number.isNaN(f) && Number.isNaN(i)) return undefined;
      return (Number.isNaN(f) ? 0 : f) * 12 + (Number.isNaN(i) ? 0 : i);
    };
    const preferredHeightMin =
      preferredHeightMinFeet !== "" || preferredHeightMinInches !== ""
        ? toTotalInches(preferredHeightMinFeet, preferredHeightMinInches)
        : undefined;
    const preferredHeightMax =
      preferredHeightMaxFeet !== "" || preferredHeightMaxInches !== ""
        ? toTotalInches(preferredHeightMaxFeet, preferredHeightMaxInches)
        : undefined;

    const payload = {
      ...rest,
      age: Number(form.age),
      siblings: siblingsArray,
      brothersCount: computedBrothers,
      sistersCount: computedSisters,
      houseSizeMarla: form.houseSizeMarla
        ? Number(form.houseSizeMarla)
        : undefined,
      views: Number(form.views || 0),
      dob: form.dob || undefined,
      preferredAgeRange:
        preferredAgeMin || preferredAgeMax
          ? {
              min: preferredAgeMin ? Number(preferredAgeMin) : undefined,
              max: preferredAgeMax ? Number(preferredAgeMax) : undefined,
            }
          : undefined,
      preferredCountries: csvToArray(preferredCountries),
      preferredCities: csvToArray(preferredCities),
      preferredProfessions: csvToArray(preferredProfessions),
      preferredCastes: csvToArray(preferredCastes),
      preferredHeightMin,
      preferredHeightMax,
    };

    try {
      setIsSaving(true);
      setGlobalLoadingMessage("Creating new profile...");
      setLoading(true);
      
      await apiFetch("/profiles", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      onSaved && onSaved();
      setForm(initialFormState);
    } catch (err) {
      console.error("Create user failed:", err);
      setFormError(err.message || "Failed to create user");
    } finally {
      setIsSaving(false);
      setLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-[var(--color-surface)] shadow-lg">
        {/* HEADER — always visible */}
        <div className="flex items-start justify-between px-4 sm:px-6 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <h3 className="text-lg font-semibold text-[var(--color-text-strong)]">
            Create Profile
          </h3>
          <button
            onClick={onClose}
            className="text-sm font-semibold text-[var(--color-text)]"
          >
            Close
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* BODY — scrolls; only the form fields move */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            {formError && (
              <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
                {formError}
              </div>
            )}
            Personal Details
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Full Name
            </label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Age
              </label>
              <input
                name="age"
                type="number"
                min="18"
                max="100"
                value={form.age}
                onChange={handleAgeChange}
                onBlur={handleAgeBlur}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">
                {attemptedSubmit && form.age !== "" && (Number(form.age) < 18 || Number(form.age) > 100)
                  ? "Age must be between 18 and 100"
                  : ""}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Date of Birth
              </label>
              <input
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Gender
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                {/* Placeholder so a fresh form doesn't silently visually-show
                    "Male" while state stays empty (and the card ends up
                    without a gender badge). Forcing an explicit pick. */}
                <option value="" disabled>
                  Select gender
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
            <div>

              <HeightSelector
                value={{ 'heightFeet': form.heightFeet, 'heightInches': form.heightInches }}

                onChange={handleChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Marital Status
              </label>
              <select
                name="maritalStatus"
                value={form.maritalStatus}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="separated">Separated</option>
                <option value="widowed">Widowed</option>
              </select>
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
          </div>
          <div className="border-t border-[var(--color-border)]"></div>
          <div>
            Caste
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Cast
            </label>
            <input
              name="cast"
              value={form.cast}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
          <div className="border-t border-[var(--color-border)]"></div>
          <div>
            <div className="text-sm font-medium text-[var(--color-text-muted)] mb-2">House Information</div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              House Status
            </label>
            <select
              name="houseStatus"
              value={form.houseStatus}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              <option value="rent">Rent</option>
              <option value="own">Own</option>
            </select>
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                House Size (Marla)
              </label>
              <input
                name="houseSizeMarla"
                type="number"
                min="0"
                value={form.houseSizeMarla}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500"></div>
            </div>
            
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                City
              </label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="Enter city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Country
              </label>
              <input
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="e.g. Pakistan"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Area
            </label>
            <input
              name="area"
              value={form.area}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="Enter area"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Further Details
              </label>
              <input
                name="houseLocation"
                value={form.houseLocation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="block or phase "
              />
              <div className="mt-1 text-sm text-red-500"></div>
            </div>

          <div>
            Education Details
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Profession
            </label>
            <input
              name="profession"
              value={form.profession}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
            <div className="mt-1 text-sm text-red-500"></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Monthly Income (Rs)
            </label>
            <input
              type="text"
              name="monthlyIncome"
              value={form.monthlyIncome}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="Enter amount in rupees"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Education
            </label>
            <input
              name="education"
              value={form.education}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>
          <div></div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Institute of Education
            </label>
            <input
              name="instituteOfQualification"
              value={form.instituteOfQualification}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
            />
          </div>

          <div className="border-t border-[var(--color-border)] mt-4 pt-4"></div>
          Family Details
          <div className="flex items-center justify-between">

            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Siblings
            </label>
            <button
              type="button"
              onClick={addSibling}
              className="text-sm text-[var(--color-primary)]"
            >
              + Add Sibling
            </button>
          </div>
          {(form.siblings || []).map((s, i) => (
            <div key={i} className="mt-2 space-y-2">
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Relation
                  </label>
                  <select
                    value={s.relation}
                    onChange={(e) =>
                      handleSiblingChange(i, "relation", e.target.value)
                    }
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  >
                    <option value="brother">Brother</option>
                    <option value="sister">Sister</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Qualification
                  </label>
                  <input
                    value={s.qualification}
                    onChange={(e) =>
                      handleSiblingChange(i, "qualification", e.target.value)
                    }
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Profession
                  </label>
                  <input
                    value={s.profession}
                    onChange={(e) =>
                      handleSiblingChange(i, "profession", e.target.value)
                    }
                    className="w-full rounded-lg border px-2 py-1 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Marital Status
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={s.maritalStatus || ""}
                      onChange={(e) =>
                        handleSiblingChange(i, "maritalStatus", e.target.value)
                      }
                      className="w-full rounded-lg border px-2 py-1 text-sm"
                    >
                      <option value="">Select</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="separated">Separated</option>
                      <option value="widowed">Widowed</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeSibling(i)}
                      className="rounded px-2 py-1 text-sm border"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="border-b border-[var(--color-border)] mt-4 pb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Father Occupation
              </label>
              <input
                name="fatherOccupation"
                value={form.fatherOccupation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">{attemptedSubmit && !form.fatherOccupation ? "" : ""}</div>
              <div className="mt-2 text-xs">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="fatherDeceased"
                    checked={!!form.fatherDeceased}
                    onChange={handleChange}
                  />
                  <span className="text-[var(--color-text-muted)]">
                    Father deceased
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Mother Occupation
              </label>
              <input
                name="motherOccupation"
                value={form.motherOccupation}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              />
              <div className="mt-1 text-sm text-red-500">{attemptedSubmit && !form.motherOccupation ? "" : ""}</div>
              <div className="mt-2 text-xs">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="motherDeceased"
                    checked={!!form.motherDeceased}
                    onChange={handleChange}
                  />
                  <span className="text-[var(--color-text-muted)]">
                    Mother deceased
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--color-border)] mt-4 pt-4"></div>

          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)]">
              Preferences
            </label>
            <input
              name="preferences"
              value={form.preferences}
              onChange={handleChange}
              className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              placeholder="eg no foreign proposals, looking for someone with education etc"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                Contact Number{" "}
                <span className="text-xs font-normal opacity-70">(optional)</span>
              </label>
              <input
                name="contactNumber"
                type="text"
                inputMode="numeric"
                pattern="\d{11}"
                maxLength={11}
                value={form.contactNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                  if (value.length <= 11) {
                    setForm({ ...form, contactNumber: value });
                  }
                }}
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                placeholder="03003331333"
              />
              <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                Leave blank to keep contact off-platform — you'll introduce
                both parties manually when approving requests.
              </div>
            </div>
          </div>

          {/* ===== Match Preferences (used when this profile receives referrals) ===== */}
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-[var(--color-text-strong)]">
              Match Preferences
            </h4>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              Used when matching this profile to others. Comma-separate multiple values.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Gender
                </label>
                <select
                  name="preferredGender"
                  value={form.preferredGender}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                >
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Min Age
                  </label>
                  <input
                    type="number"
                    name="preferredAgeMin"
                    value={form.preferredAgeMin}
                    onChange={handlePreferredAgeChange("preferredAgeMin")}
                    onBlur={handlePreferredAgeBlur("preferredAgeMin")}
                    onKeyDown={blockNonNumericKey}
                    min="18"
                    max="80"
                    inputMode="numeric"
                    className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Max Age
                  </label>
                  <input
                    type="number"
                    name="preferredAgeMax"
                    value={form.preferredAgeMax}
                    onChange={handlePreferredAgeChange("preferredAgeMax")}
                    onBlur={handlePreferredAgeBlur("preferredAgeMax")}
                    onKeyDown={blockNonNumericKey}
                    min="18"
                    max="80"
                    inputMode="numeric"
                    className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Countries
                </label>
                <input
                  type="text"
                  name="preferredCountries"
                  value={form.preferredCountries}
                  onChange={handleChange}
                  placeholder="Pakistan, UK, USA"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Cities
                </label>
                <input
                  type="text"
                  name="preferredCities"
                  value={form.preferredCities}
                  onChange={handleChange}
                  placeholder="Lahore, Karachi"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Professions
                </label>
                <input
                  type="text"
                  name="preferredProfessions"
                  value={form.preferredProfessions}
                  onChange={handleChange}
                  placeholder="Engineer, Doctor"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Preferred Castes
                </label>
                <input
                  type="text"
                  name="preferredCastes"
                  value={form.preferredCastes}
                  onChange={handleChange}
                  placeholder="Awan, Rajput"
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                  Preferred Height Range
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-[var(--color-text-muted)] mb-1">Min</div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="preferredHeightMinFeet"
                        value={form.preferredHeightMinFeet}
                        onChange={handleChange}
                        min="0"
                        max="8"
                        placeholder="ft"
                        className="w-full rounded-lg border border-[var(--color-border)] px-2 py-2 text-sm"
                      />
                      <input
                        type="number"
                        name="preferredHeightMinInches"
                        value={form.preferredHeightMinInches}
                        onChange={handleChange}
                        min="0"
                        max="11"
                        placeholder="in"
                        className="w-full rounded-lg border border-[var(--color-border)] px-2 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--color-text-muted)] mb-1">Max</div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="preferredHeightMaxFeet"
                        value={form.preferredHeightMaxFeet}
                        onChange={handleChange}
                        min="0"
                        max="8"
                        placeholder="ft"
                        className="w-full rounded-lg border border-[var(--color-border)] px-2 py-2 text-sm"
                      />
                      <input
                        type="number"
                        name="preferredHeightMaxInches"
                        value={form.preferredHeightMaxInches}
                        onChange={handleChange}
                        min="0"
                        max="11"
                        placeholder="in"
                        className="w-full rounded-lg border border-[var(--color-border)] px-2 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Internal Notes
                </label>
                <textarea
                  name="clientNotes"
                  value={form.clientNotes}
                  onChange={handleChange}
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          </div>
          {/* FOOTER — always visible */}
          <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              <CancelIcon className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <SaveIcon className="w-4 h-4" />
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
